import { AvailableCompaniesService } from '../services/available-companies.service';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, Optional, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ProjectService, ProjectListLoad } from '.././services/project.service';
import {
  Subject,
  Subscription,
  catchError,
  distinctUntilChanged,
  filter,
  of,
  switchMap,
  take,
  takeUntil,
  timeout,
  timer,
} from 'rxjs';
import {
  DEFAULT_PROJECT_GRID_VIEW,
  ProjectGridModel,
  ProjectGridView,
  StatusFilter,
} from '../models/project-grid.types';
import {
  buildProjectGridModel,
  EMPTY_CRITERIA,
  searchHaystack,
} from '../services/project-grid-view';
import { NO_PROJECT_TAB_REASON, SELECTION_ROUTE } from '../services/project-selection-gate';
import { currentTabFromUrl, isProjectTabUrl } from '../services/project-route';
import { decideRestore } from '../services/project-restore-decision';
import { LAST_SELECTED_PROJECT_KEY } from '../project-selected.guard';
import { UploadDialogService } from '../services/upload-dialog.service';
import { TemperatureDataRow } from '.././models/temperature-data-row';
import { ProjectTemperatureData } from '.././models/project-temperature-data';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import {
  AuthStatus,
  AuthStatusService,
  INITIAL_AUTH_STATUS,
} from '../auth/auth-status.service';

// Required for MSAL
import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import {
  AccountInfo,
  EventMessage,
  EventType,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { UserAuthService } from '../UserAuth.service';
import { ActiveCustomer } from '../models/active-customer';
import { normalizeDateFormat, normalizeTimeFormat } from '../i18n/date-format';
import { ProjectInfo } from '../models/project-info';
import { DeviceStatus } from '../services/project.service';
import {
  PROJECT_QUERY_PARAM,
  buildProjectLink,
  initialDeepLinkProject,
  projectParamFromSearch,
  readProjectParam,
  resolveDeepLink,
} from '../services/project-deep-link';
import {
  ProjectUploadService,
  UploadError,
  UploadProgress,
  formatBytes,
  projectNameFromFile,
  validateFile,
} from '../services/project-upload.service';
import { PreferencesService } from '../services/preferences.service';
import { AlertsService } from '../services/alerts.service';
import { OfflineStateService } from '../services/offline-state.service';
import {
  maxEpochOf,
  hasHighWaterMark,
  newestRowOf,
} from '../services/telemetry-cursor';
import { ChartViewportService } from '../services/chart-viewport.service';
import { HistoricalLoadService } from '../services/historical-load.service';
import {
  LiveTelemetryService,
  LiveTick,
} from '../services/live-telemetry.service';
import {
  TelemetryFamilyName,
  convertRawRow,
  cursorFamilyFor,
} from '../services/telemetry-conversion';
import { ProjectLossData } from '../models/project-loss-data';
import { ProjectZoneData } from '../models/project-zone-data';
import {
  isProjectLive,
  partitionProjects,
  byNewestTelemetry,
  lastTelemetryEpoch,
} from '../services/project-liveness';
import { ToastService } from '../services/toast.service';
import { ExportPrerenderService } from '../services/export-prerender.service';
import { ProjectListPushService, PushState } from '../services/project-list-push.service';
import { environment } from '../../environments/environment';
import { isEntitledToCureSheets } from '../cure-sheets/cure-sheet-entitlement';
import { cureSheetEntitlement$ } from '../cure-sheets/cure-sheet-entitlement-store';
import { closeSubscription, runTeardown } from '../services/safe-teardown';

/**
 * Routes that show a selected project. Selecting or restoring a project keeps
 * the user on one of these; anywhere else (admin, settings) falls back to the
 * default tab, because a project selection has nothing to show there.
 */

/** Upper bound on waiting for the project list before offering the picker. */
const PROJECT_LIST_SETTLE_MS = 15000;

/**
 * How often a tab quietly re-asks for the project list, in ms. 0 or absent = OFF.
 *
 * RESTORED. ca738a6 deleted this on the premise that the push plus
 * reconcile-on-reconnect covers every case. That premise was MEASURED FALSE on
 * 2026-09-22: on a real bench cure the socket stayed OPEN and healthy — pings
 * advancing — and delivered ZERO data frames for eleven minutes while the UI
 * displayed "Live updates on". Reconcile-on-reconnect cannot fire in that case,
 * because there is no disconnect: the one shape the replacement does not cover is
 * a socket that is alive and silent.
 *
 * THE OBJECTION IN ca738a6 IS REAL AND IS NOT ANSWERED BY THIS COMMIT: a
 * continuous poll MASKS the failure it covers — beta-dev ran a fourteen-day silent
 * SignalR outage that nobody noticed because the list kept looking fresh. That is
 * why this is a FLOOR and not the mechanism anyone relies on, and why the
 * connection-state indicator (pushState, surfaced in the header) must stay: the
 * floor keeps the DATA correct, the indicator keeps the FAILURE visible. Removing
 * either one alone is defensible; removing both is what produced the measurement
 * above.
 *
 * PER-ENVIRONMENT AND OFF BY DEFAULT. 45 s sits in the agreed 30-60 s band and
 * beats the server's ~2.5 min enrichment floor. Cost, so nobody re-derives it:
 * 3600 / 45 = 80 requests per tab per hour.
 */
export function projectListRefreshMs(): number {
  const configured = (environment as { projectListRefreshMs?: number })
    .projectListRefreshMs;
  return typeof configured === 'number' && configured > 0 ? configured : 0;
}

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  // TWO FILES, ONE COMPONENT, ORDER SIGNIFICANT. Split purely for the
  // per-component style budget — see the header of the overlays file. Angular
  // concatenates styleUrls in the order listed, so the cascade is unchanged from
  // when this was a single 23.04 kB stylesheet. Do not reorder.
  styleUrls: [
    './main-layout.component.scss',
    './main-layout-overlays.component.scss',
  ],
})
export class MainLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * Whether the Cure Sheets nav item is shown.
   *
   * FALSE UNTIL THE ENTITLEMENT IS KNOWN. Cure sheets are subscription value, so
   * this follows the same closed-by-default rule as the screen itself (see
   * cure-sheets/cure-sheet-entitlement.ts) — the nav item and the screen must
   * agree, or a customer clicks through to a "not included" page.
   *
   * Driven by CureSheetsService.entitlement$, the SAME value the screen uses, so
   * the two cannot disagree.
   */
  showCureSheets = false;

  title = 'vericure-webui';
  showHeader = false;
  selectedProject: string = '';
  uploadedroject: string = '';
  isRunning: boolean = false;
  tick: number = 0;
  tickMax: number = 0;
  tempDataLoading: Boolean = false;
  zoneDataLoading: Boolean = false;
  lossDataLoading: boolean = false;
  notesDataLoading: boolean = false;
  projUploading: boolean = false;
  projectLoadPerc: number = 0;
  /**
   * True while ANY telemetry request is in flight.
   *
   * Complements the per-family flags rather than replacing them: those are set by
   * this component and so cover a project change, while this also catches the
   * fetches a tab component starts for itself — which is what left /health and
   * /curing showing empty charts with no loading signal on first open.
   */
  telemetryInFlight = false;

  fiberFaultLevel: number = 0; // 0: Normal, 1: Warning, 2: Error
  cmsFaultLevel: number = 0; // 0: Normal, 1: Warning, 2: Error
  deviceStatus: DeviceStatus = 'unknown';

  /**
   * The word shown beside the status dot. Never empty.
   *
   * 'unknown' is NOT 'offline' and must not be labelled as one. Offline is a
   * measured fact — three consecutive empty polls AND 60 s without data
   * (ProjectService.checkDeviceTimeout). 'unknown' is the absence of that
   * measurement: the pill only renders once realtime is expected, so at that
   * point it means "we are asking and nothing has come back yet". Calling that
   * "Offline" would assert something we have not established, and would read
   * "Offline" on a healthy rig for the first minute of every cure.
   */
  get deviceStatusLabel(): string {
    switch (this.deviceStatus) {
      case 'online':
        return 'Online';
      case 'offline':
        return 'Offline';
      default:
        return 'Waiting for data';
    }
  }
  /**
   * LIVE PROJECT-LIST CHANNEL STATE — surfaced, never silent.
   *
   * Distinct from `deviceStatus`, and the distinction matters. `deviceStatus`
   * answers "is the RIG sending telemetry"; this answers "is THIS TAB still being
   * told when the project list changes". A tab can be perfectly connected to a rig
   * that has finished, and a tab can be watching a healthy rig while its own list
   * channel is dead — the second is invisible without this.
   *
   * TWO-TYPE NOTE for whoever renders this: a HISTORICAL-viewing tab has no
   * telemetry expectation, so "disconnected" means only "new cures may not appear";
   * a LIVE-viewing tab is additionally watching a running cure, so the same word
   * also means "this list may not notice when it ends". Same state, two costs.
   */
  pushState: PushState = 'idle';

  /** The word shown for the list channel. Never empty, never asserts more than is known. */
  get listChannelLabel(): string {
    switch (this.pushState) {
      case 'live':
        return 'Live updates on';
      case 'connecting':
        return 'Reconnecting…';
      case 'failed':
        return 'Live updates off';
      case 'disabled':
        return '';
      default:
        return '';
    }
  }

  /**
   * Whether to show it at all. 'idle' and 'disabled' render NOTHING: idle means we
   * have not tried yet, and disabled means the feature is off for this environment.
   * Neither is a fault and labelling them as one would cry wolf — the same reasoning
   * that keeps 'unknown' out of the Offline label above.
   */
  get showListChannel(): boolean {
    return this.pushState === 'live' || this.pushState === 'connecting' || this.pushState === 'failed';
  }

  /** Severity class for the dot: only a genuine failure is warned about. */
  get listChannelClass(): string {
    if (this.pushState === 'failed') return 'channel-failed';
    if (this.pushState === 'connecting') return 'channel-connecting';
    return 'channel-live';
  }

  isOfflineAtTick: boolean = false; // device was offline at the scrubbed tick (time-dimensioned)
  private readonly _destroying$ = new Subject<void>();

  /** DIAGNOSTIC (temporary): pollers created and components alive. */
  static pollerCount = 0;
  static instanceCount = 0;
  readonly instanceId = ++MainLayoutComponent.instanceCount;

  /** The poll timers are singletons; see initializeProjects. */
  private pollTimersStarted = false;
  private playbackTimerSub?: Subscription;
  private realtimePollSub?: Subscription;
  private projectListRefreshSub?: Subscription;
  private listFreshnessStarted = false;

  projTemperatureData: ProjectTemperatureData = {
    TRC: [],
    SI: 0,
    MI: 0,
    CRB: 0,
    CRE: 0,
    CRS: 0,
    LSS: 0,
    LES: 0,
    CT: 0,
    OT: 0,
    SER: '',
    FIB: '',
    HMC: [],
    MAR: [],
    N: [],
    U: [],
    LG: '',
    VS: 0,
  };
  currTime: Date = new Date(0, 0, 0);
  currDuration: number = 0;

  /** Newest tick UT accepted from any live route, so a replay is not counted twice. */
  private newestLiveUT = 0;
  /** Live rows accepted beyond the backfill — the slider's range above it. */
  private liveTickCount = 0;

  uploadedProjectSubscription?: Subscription;
  fiberFaultLevelSubscription?: Subscription;
  cmsFaultLevelSubscription?: Subscription;

  /**
   * Mirror of the TEMPERATURE cursor, kept for the existing template/diagnostics.
   * The authoritative cursors live in ProjectService.cursors — one per family,
   * because the three server HWMs advance independently.
   */
  /** Latest loss payload, used to size the loss downsample window. */
  projLossData: ProjectLossData | null = null;
  lastEpocTime: number = 0;
  fetchRealTimeData: boolean = false;

  isAuthenticated = false;
  isAdmin = false;
  isSuperAdmin = false;
  /** Counts MainLayoutComponent instances for this page load. */
  private static initCount = 0;

  activeAccount: AccountInfo | null = null;
  activeCustomerName = '';
  
  // Modal properties
  showProjectModal = false;
  activeTab = 'existing';
  isModalMaximized = false;
  isModalMinimized = false;
  isModalResizing = false;
  modalStartX = 0;
  modalStartY = 0;
  modalStartWidth = 0;
  modalStartHeight = 0;
  
  userDropdownOpen = false;

  /**
   * Open or close the user menu. OPENING refreshes the switchable companies: the
   * list is otherwise the copy taken at sign-in, so a company granted since then
   * would not appear, and a user with one company would never see the switcher
   * at all. See AvailableCompaniesService.
   */
  toggleUserDropdown(): void {
    this.userDropdownOpen = !this.userDropdownOpen;
    if (this.userDropdownOpen) {
      void this.availableCompanies?.refresh();
    }
  }

  /**
   * What the app can honestly say about the session, for the header.
   *
   * Bound rather than polled so a token that goes stale mid-session updates the
   * indicator the moment the interceptor finds out, instead of at the next
   * navigation — a stale "signed in" is precisely the lie this replaces.
   */
  authStatus: AuthStatus = INITIAL_AUTH_STATUS;

  /** Whether the project list LOADED — distinct from whether it is empty. */
  projectLoad: ProjectListLoad = { kind: 'idle' };
  @ViewChild('userMenuRef') userMenuRef!: ElementRef;

  // Offscreen host for export prerendering (see ExportPrerenderService)
  @ViewChild('offscreenChartHost', { read: ViewContainerRef })
  offscreenChartHost!: ViewContainerRef;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.userMenuRef && !this.userMenuRef.nativeElement.contains(event.target)) {
      this.userDropdownOpen = false;
    }
  }

  // Export panel properties
  showExportPanel = false;
  exportPanelMode: 'pdf' | 'csv' = 'pdf';

  // Search and filter properties
  searchTerm = '';
  selectedFilter = 'all'; // 'all', 'live', 'historical'
  dateFilterFrom: string = '';
  dateFilterTo: string = '';
  showAdvancedFilters = false;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    public authService: UserAuthService,
    public projectService: ProjectService,
    private preferencesService: PreferencesService,
    private alertsService: AlertsService,
    private offlineState: OfflineStateService,
    private toastService: ToastService,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    private msalBroadcastService: MsalBroadcastService,
    private exportPrerender: ExportPrerenderService,
    private chartViewport: ChartViewportService,
    private liveTelemetry: LiveTelemetryService,
    private uploadService: ProjectUploadService,
    private uploadDialog: UploadDialogService,
    public authStatusService: AuthStatusService,
    private historicalLoad: HistoricalLoadService,
    private projectListPush: ProjectListPushService,
    // TRAILING and OPTIONAL: specs build this component with 18 positional
    // arguments. DI always supplies it in the app.
    @Optional() private availableCompanies?: AvailableCompaniesService
  ) {}

  ngAfterViewInit(): void {
    console.log(
      `Main Layout > instance #${this.instanceId} alive (total created: ${MainLayoutComponent.instanceCount})`
    );
    if (this.offscreenChartHost) {
      this.exportPrerender.registerHost(this.offscreenChartHost);
    }
  }

  /** Teardown must never abort a route deactivation — see safe-teardown.ts. */
  ngOnDestroy(): void {
    runTeardown('MainLayoutComponent.ngOnDestroy', () => {
      console.log(`Main Layout > instance #${this.instanceId} destroyed`);
      this.playbackTimerSub?.unsubscribe();
      this.realtimePollSub?.unsubscribe();
      this.exportPrerender.clearHost();
      closeSubscription('uploadedProjectSubscription', this.uploadedProjectSubscription);
      closeSubscription('fiberFaultLevelSubscription', this.fiberFaultLevelSubscription);
      closeSubscription('cmsFaultLevelSubscription', this.cmsFaultLevelSubscription);
      this.alertsService.stopPolling();
      this.projectListRefreshSub?.unsubscribe();
      void this.projectListPush.stop();
      void this.liveTelemetry.stop();
      this._destroying$.next(undefined);
      this._destroying$.complete();
    });
  }

  onProjectChange(proj: string, force = false, fromRestore = false) {
    // Every project load passes through here, so this line plus its caller frame
    // identifies which entry path started it.
    console.log(`PROJECT-CHANGE ${proj}`);

    // NO PROJECT IS NOT A PROJECT. The open path resets cursors, stops live
    // telemetry, navigates and reloads history — none of which mean anything for
    // an empty name, and all of which cost a round trip. The dropdown can emit ''
    // too, so this stays even with the subject's seed filtered upstream.
    if (!proj || proj.length === 0) {
      console.log('PROJECT-CHANGE ignored: empty project name');
      return;
    }

    // ALREADY OPEN IS NOT A CHANGE. The open path resets cursors, restarts live
    // telemetry, navigates and refetches every family — none of which mean
    // anything for the project already on screen.
    //
    // `force` exists for the upload paths: a re-upload under the SAME name is a
    // genuinely different dataset, so those callers must be able to say so.
    if (!force && proj === this.selectedProject) {
      console.log(`PROJECT-CHANGE ignored: ${proj} is already open`);
      return;
    }
    console.log('app comp > Proj Change Called: ' + proj);
    
    // Always use the current modal settings (radio button selection)
    const selectedUnit = this.projectService.toUpload_isMetricUnit;
    const selectedLang = this.projectService.toUpload_selectedLang;
    
    // Sync both unit properties
    this.projectService.isMetricUnit = selectedUnit;
    this.projectService.toUpload_isMetricUnit = selectedUnit;
    this.projectService.selectedLang = selectedLang;
    this.projectService.toUpload_selectedLang = selectedLang;
    
    // Save these settings for this project
    this.projectService.setProjectUnitPreference(proj, selectedUnit);
    this.projectService.setProjectLanguagePreference(proj, selectedLang);
    
    console.log('Project unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
    console.log('Project language setting:', this.projectService.selectedLang);
    console.log('Unit code for API:', this.projectService.isMetricUnit ? '0 (Metric)' : '1 (Imperial)');

    this.projectService.setFiberFaultLevel(0);
    this.projectService.resetDeviceStatus();

    this.fetchRealTimeData = false;
    this.lastEpocTime = 0;
    // A different cure has its own tick range and its own start. Carrying either
    // across would make the slider describe the previous project.
    this.liveTickCount = 0;
    this.newestLiveUT = 0;
    this.projectService.resetCursors();
    // A genuinely new selection: the already-open guard above has passed, so the
    // load service's latch should be answering "have we loaded THIS project",
    // not still holding the previous one.
    this.historicalLoad.reset();
    // A new project reopens the "is it still loading?" question the spinner
    // answers; without this it would stay suppressed from the previous load.
    this.projectService.resetInitialLoad();
    this.chartViewport.reset();
    this.tempDataLoading = proj.length > 0;
    this.zoneDataLoading = false;
    this.lossDataLoading = false;
    this.notesDataLoading = false;
    this.projectLoadPerc = 0;
    // The URL always names the project on screen.
    //
    // Selecting a project WRITES its deep link, so the address bar is already
    // shareable and a refresh lands back on the same project instead of the
    // picker. Previously this navigated with no query at all, which both erased
    // a link the user had just followed and left refresh with nothing to go on.
    //
    // Claim the name first: this navigation makes queryParams emit, and without
    // the latch the deep-link handler would answer its own echo and re-run the
    // whole load.
    if (proj) {
      this.deepLinkHandled = proj;
      this.deepLinkedProject = proj;
    }
    // STAY ON THE TAB THE USER IS ON.
    //
    // This was hardcoded to '/temperature', which meant selecting OR RESTORING a
    // project always dumped you there. Measured on the live app after the
    // restore fix: reloading /health or /graph preserved the URL right up until
    // the restore fired, and then navigated to /temperature — so a reload still
    // moved you off your tab, which is the second half of what Joel actually
    // reported ("reloads reload to their current location").
    //
    // Only project-dependent tabs are preserved. Selecting a project from an
    // admin screen still goes to /temperature, because admin is not a project
    // view and staying there would show a selection that does nothing.
    //
    // A RESTORE IS NOT A SELECTION, and conflating them broke the admin console.
    // Restore runs automatically on load; if the user is on /admin/devices it
    // must leave them there. Before this, restore called the same path as an
    // explicit pick, currentTabFromUrl answered with its default, and the tab
    // self-evicted to /temperature the moment a project was restored — with the
    // project's deep link attached, which is what made it look like a guard.
    // /admin/** is estate management; it is not project-gated and must not be
    // project-navigated either.
    if (!fromRestore || isProjectTabUrl(this._router.url)) {
      this._router.navigate([`/${currentTabFromUrl(this._router.url)}`], {
        queryParams: proj ? { [PROJECT_QUERY_PARAM]: proj } : {},
        queryParamsHandling: 'merge',
      });
    }
    this.selectedProject = proj;
    this.projectService.setProject(proj);
    
    // Save the selected project to localStorage for persistence across refreshes
    if (proj && proj.length > 0) {
      localStorage.setItem('lastSelectedProject', proj);
      console.log('Main Layout > Saved project to localStorage:', proj);
    }
    
    this.loadHistoricalTelemetry(proj, force);

    // Item 4: (re)connect the live stream for the new project. No-op while the
    // flag is off; each family is seeded from its own cursor after the backfill.
    // Only a live cure gets a socket. Opening one for a finished project bought
    // nothing and cost a catch-up poll on every connect, reconnect and close.
    // Deferred when the list has not arrived yet — the poll tick re-checks.
    if (this.selectedProjectIsLive()) {
      void this.liveTelemetry.start(proj, {});
    } else {
      void this.liveTelemetry.stop();
    }
  }

  /**
   * Ingest ordered live ticks.
   *
   * The broadcast carries RAW ushort at full resolution, so each row is converted
   * to engineering units here using the same formulas the REST path applies
   * server-side — otherwise live ticks would plot in different units from polled
   * ones on the same chart. Converted rows then go through the SAME RT setters the
   * poll uses, so the ring buffer, per-family cursors and charts are untouched.
   *
   * Downsampling note: `data` is full resolution. Viewport decimation is re-derived
   * client-side exactly as it already is for a full-res REST response — a row with
   * no SIX is uniform, which is precisely the shape these rows have.
   */
  onLiveTicks(ticks: LiveTick[]): void {
    if (!ticks || ticks.length === 0) {
      return;
    }
    const byFamily = new Map<TelemetryFamilyName, LiveTick[]>();
    for (const tick of ticks) {
      const list = byFamily.get(tick.family) ?? [];
      list.push(tick);
      byFamily.set(tick.family, list);
    }
    byFamily.forEach((familyTicks, family) => {
      this.ingestFamily(family, familyTicks);
    });
  }

  /** Server ZONESTORAGE frames dropped because zones are derived client-side. */
  ignoredServerZoneFrames = 0;

  /** Convert and route one family's ticks into its existing RT path. */
  private ingestFamily(family: TelemetryFamilyName, ticks: LiveTick[]): void {
    const isMetric = this.projectService.isMetricUnit;
    const newestEpoch = ticks[ticks.length - 1].timestamp;

    if (family === 'TEMPERATURE') {
      const rows = ticks.map((t) => ({
        T: new Date(t.timestamp),
        E: t.timestamp,
        UT: t.timestamp,
        TR: convertRawRow(t.data, family, isMetric),
      })) as ProjectTemperatureData['TRC'];

      this.projectService.setDeviceOnline();
      this.projectService.setRTTempData({
        ...this.projTemperatureData,
        TRC: rows,
        HWM: newestEpoch,
      } as ProjectTemperatureData);
      this.currTime = rows[rows.length - 1].T;
      this.tick += rows.length;
      // ZONES ARE DERIVED FROM THESE TICKS. Fold them now by polling the raw
      // stored values after the zone cursor (a converted frame cannot be folded).
      this.projectService.loadZoneDataRT(this.selectedProject);
    } else if (family === 'ZONESTORAGE') {
      // SERVER ZONES ARE NOT USED: the browser derives zones (Joel, 2026-09-24).
      // Dropped here, and deliberately BEFORE the cursor advance below: the zone
      // cursor is the raw fold's, and advancing it from these frames would make
      // the next raw poll skip exactly the ticks it has to fold. Counted, not silent.
      this.ignoredServerZoneFrames += ticks.length;
      return;
    } else {
      this.projectService.setRTLossData({
        ...(this.projLossData ?? {}),
        LRC: ticks.map((t) => ({
          T: new Date(t.timestamp),
          UT: t.timestamp,
          LR: convertRawRow(t.data, family, isMetric),
          FD: [],
          FP: [],
          FM: [],
        })),
        HWM: newestEpoch,
      } as ProjectLossData);
    }

    // Advance the family's poll cursor so a later poll will not re-request these,
    // and re-seed the gate so a replayed broadcast is recognised as a duplicate.
    const cursorFamily = cursorFamilyFor(family);
    const advanced = this.projectService.advanceCursor(
      cursorFamily,
      { HWM: newestEpoch },
      newestEpoch
    );
    this.liveTelemetry.seed(family, advanced);
    if (family === 'TEMPERATURE') {
      this.lastEpocTime = advanced;
    }
  }

  /**
   * Can the SELECTED project still produce telemetry?
   *
   * The server already answers this — isLive on GetProjectlist, derived from the
   * cure's finished flag and latest-per-CMS. Nothing was asking. A completed
   * cure opened a SignalR connection and enabled a 5 s poll for data that cannot
   * exist, which is the whole of the completed-project storm: not a loop, work
   * that should never have been scheduled.
   *
   * Unknown means NO. A project absent from the list is one we cannot vouch for,
   * and the safe direction is silence — a live cure appears in the list with
   * isLive true, and the check below re-evaluates as soon as it does.
   */
  /** The list row for a project, or null when the list has not arrived yet. */
  private projectInfoFor(name: string) {
    return (
      (this.projectService.projectList ?? []).find((p) => p?.ProjectName === name) ??
      null
    );
  }

  /**
   * Should the header show a device-liveness pill at all?
   *
   * A FINISHED CURE HAS NO LIVENESS TO REPORT. The pill previously rendered for
   * any selected project, so a completed historical cure sat permanently on
   * "Waiting for data" — a statement that is not merely unhelpful but FALSE: that
   * project will never receive data, so nothing is being waited for. Reported by
   * Joel on S3077, and it is 25e5c8e/83d4b71 doing exactly what they were asked
   * and being wrong for a case neither considered.
   *
   * The recency detector holding 'unknown' when it cannot measure a cadence is
   * still right — for a LIVE project with too few arrivals yet. The defect is
   * showing that state on a project where the question does not apply.
   *
   * Liveness comes from isProjectLive — SERVER-DECIDED, and the same input that
   * drives the grid's "Live Projects (n) / Historical Projects (n)" split, so the
   * header and the grid cannot disagree about what a project is.
   *
   * Hidden while the project list has not arrived, deliberately: isProjectLive is
   * false for an unknown project, and showing nothing is better than asserting a
   * liveness we cannot yet support.
   */
  get showDeviceStatus(): boolean {
    return (
      !!this.selectedProject &&
      this.fetchRealTimeData &&
      this.selectedProjectIsLive()
    );
  }

  private selectedProjectIsLive(): boolean {
    if (!this.selectedProject) {
      return false;
    }
    const project = this.projectService.projectList.find(
      (p) => p?.ProjectName === this.selectedProject
    );
    return isProjectLive(project);
  }

  /**
   * Should the realtime FALLBACK poll run?
   *
   * Evaluated per tick rather than latched, so a project that becomes live — or
   * a list that arrives after the deep link opened the project — starts polling
   * without needing another trigger.
   */
  private realtimeWanted(): boolean {
    return this.fetchRealTimeData && this.selectedProjectIsLive();
  }

  /**
   * One-shot temperature poll used for live catch-up. Reuses the normal cursor, so
   * it returns exactly what the socket missed and nothing that was already seen.
   */
  pollTemperatureOnce(): void {
    if (!this.selectedProject || this.selectedProject.length === 0) {
      return;
    }
    this.projectService
      .loadTemperatureData(
        this.selectedProject,
        true,
        this.projectService.getCursor('temperature')
      )
      .subscribe({
        next: (data) => {
          if (!data?.TRC?.length) {
            return;
          }
          this.projectService.setDeviceOnline();
          this.projectService.setRTTempData(data);
          this.lastEpocTime = this.projectService.advanceCursor(
            'temperature',
            data,
            maxEpochOf(data)
          );
          // Keep the gate aligned so replayed broadcasts are dropped.
          this.liveTelemetry.seed('TEMPERATURE', this.lastEpocTime);
        },
        error: (err) => console.log('live catch-up poll failed', err),
      });
  }

  /**
   * Natural spatial window of the temperature payload — the liner slice, which is
   * what the temperature chart draws. Null until the payload has arrived, in which
   * case the request goes out at full resolution.
   */
  private temperatureWindow() {
    const d = this.projTemperatureData;
    if (!d || !(d.LES > d.LSS)) {
      return null;
    }
    return { start: d.LSS, end: d.LES };
  }

  /**
   * Natural window for the loss payload. Mirror mode renders the FULL array width
   * rather than stopping at LES, so the window must span the whole trace there.
   */
  private lossWindow() {
    const d = this.projLossData;
    if (!d) {
      return null;
    }
    const firstRow = d.LRC && d.LRC.length > 0 ? d.LRC[0] : null;
    const arrayLength = firstRow?.LR?.length ?? 0;
    if (d.MIR && arrayLength > 0) {
      return { start: d.LSS ?? 0, end: arrayLength };
    }
    if (!(d.LES > d.LSS)) {
      return null;
    }
    return { start: d.LSS, end: d.LES };
  }

  /**
   * Historical backfill for a project — ONE awaited sequence, no cascade.
   *
   * Everything that used to live here (the four latches, the temperature
   * subscribe, and the zone -> loss fan-out driven by subject emissions) is now
   * HistoricalLoadService.load(). What stays is what is genuinely main-layout's:
   * the spinner flags, the live-gate seed, and saying so on screen when a project
   * comes back with nothing.
   *
   * NOTE: this is a FULL reload — GetTemperatureData supports only
   * `lastEpocTime` (rows after an epoch) or 0 (everything); there is no tick-range
   * query. Narrow this once the server grows one.
   */
  loadHistoricalTelemetry(proj: string, force = false): Promise<void> {
    if (!proj || proj.length === 0) {
      return Promise.resolve();
    }

    // THE VERDICT IS NOT CLEARED UP FRONT.
    //
    // Doing that wiped a correct "no data" banner every time a duplicate open was
    // collapsed — and since a suppressed load returns early, the flags were left
    // cleared and tempDataLoading was left RAISED, so the spinner ran forever on
    // any second call. The banner is gated on the spinners being down anyway, so
    // it is already hidden for the duration of a load; there is nothing to clear.
    this.tempDataLoading = true;

    return this.historicalLoad
      .load(proj, {
        force,
        lossWindow: this.lossWindow(),
        info: this.projectInfoFor(proj),
      })
      .then((result) => {
        if (result.suppressed) {
          // Nothing was fetched, so there are no findings to publish — but the
          // spinner this call raised is still this call's to lower.
          this.tempDataLoading = false;
          return;
        }
        this.tempDataLoading = false;
        this.zoneDataLoading = false;
        this.lossDataLoading = false;

        // THE LIVE SEED STAYS HERE. It is a main-layout concern, and a report
        // pre-render has no business telling the live gate what it has covered.
        this.lastEpocTime = result.cursor;
        this.liveTelemetry.seed('TEMPERATURE', this.lastEpocTime);

        // EMPTY IS A STATE THE USER MUST SEE.
        //
        // The old cascade answered an empty backfill by skipping zone and loss and
        // then waiting forever behind "loss backfill has not landed" — an empty
        // chart with a live-looking app around it, which is the worst of both
        // readings. A project with no rows now says so.
        this.historyEmpty = result.empty;
        this.historyFailed = result.failed;
        // A DEGRADED LOAD MUST NOT BLANK WORKING CHARTS.
        //
        // One family failing while the others loaded is a partial project, not a
        // broken one. Covering two correct charts with "Could not load this
        // project" would hide more than it explains — so this is said once, in
        // passing, and the data that did arrive stays on screen.
        if (result.degraded) {
          const lost = (Object.keys(result.outcomes) as (keyof typeof result.outcomes)[])
            .filter((f) => result.outcomes[f] === 'failed');
          this.toastService.show(
            `Some telemetry could not be loaded (${lost.join(', ')})`,
            'warning'
          );
        }
        console.log(
          'app comp > historical load settled: ' +
            JSON.stringify(result.outcomes) +
            ` cursor=${this.lastEpocTime} empty=${result.empty}`
        );
      })
      .catch((error) => {
        this.tempDataLoading = false;
        this.zoneDataLoading = false;
        this.lossDataLoading = false;
        this.historyFailed = true;
        console.log(error);
      });
  }

  /**
   * The project opened and every family answered with no rows.
   *
   * Distinct from historyFailed: nothing went wrong, there is simply nothing to
   * draw. Bound in the template so the state is visible rather than inferred from
   * an empty chart.
   */
  historyEmpty = false;
  /** A family's backfill errored. Different message, different remedy. */
  historyFailed = false;

  ngOnInit(): void {
    // The nav item reads the SAME verdict the screen does, so a link can never
    // lead to a page that says the feature is not included. The screen's own load
    // publishes it; the nav does not fetch anything of its own.
    cureSheetEntitlement$.subscribe((entitlement) => {
      this.showCureSheets = isEntitledToCureSheets(entitlement);
    });

    console.log(`MAINLAYOUT-INIT #${++MainLayoutComponent.initCount}`);
    // ALWAYS. This used to be gated on the production flag, which meant the header
    // — and with it sign-out, switch-user and change-company — was collapsed on
    // every production build. prod2 is the only config with production: true, so
    // the one environment real users reach was the one with no account menu, and
    // the routes behind those controls (/logout, /select-customer) were only
    // reachable by typing the URL.
    //
    // It read as a cutover regression when Joel hit it, but the gate dates to
    // 3ef27d4 and the byte-compare of the previous and current prod bundles shows
    // the identical logic in both. It was a debug switch that outlived its
    // purpose, not a deliberate production choice.
    this.showHeader = true;

    // Offline visual state: red sidebar (via showFiberFaultColorClass) driven by the scrubbed
    // tick, and toasts on real offline<->online transitions.
    this.offlineState.isOfflineAtTick$
      .pipe(takeUntil(this._destroying$))
      .subscribe((offline) => (this.isOfflineAtTick = offline));
    this.offlineState.transition$
      .pipe(takeUntil(this._destroying$))
      .subscribe((t) =>
        t === 'offline'
          ? this.toastService.show('Device went offline', 'warning')
          : this.toastService.show('Device reconnected', 'success')
      );

    // Item 4: ordered live ticks feed the SAME path the poll uses, so every
    // downstream consumer (ring buffer, cursors, charts) is unchanged.
    this.liveTelemetry.ticks$
      .pipe(takeUntil(this._destroying$))
      .subscribe((ticks) => this.onLiveTicks(ticks));

    // Item 4 fallback: on connect / reconnect / drop, run one catch-up poll from
    // the family cursor. That read is gap-free (item 2 HWM), so a socket outage of
    // any length is closed by a single request.
    this.liveTelemetry.catchUpNeeded$
      .pipe(takeUntil(this._destroying$))
      .subscribe((reason) => {
        console.log('Main Layout > live catch-up:', reason);
        this.pollTemperatureOnce();
      });

    // Refetch-on-scrub-back: a chart scrubbed to a tick older than the ring-buffer
    // retention window asks for the history again.
    this.projectService.historyReload
      .pipe(takeUntil(this._destroying$))
      .subscribe((reason) => {
        console.log('Main Layout > reloading history:', reason);
        this.loadHistoricalTelemetry(this.selectedProject, true);
      });

    // Capture the deep link ONCE, before anything can rewrite the URL. Kept even
    // though the handler that used to rewrite it is gone: reading at init is
    // simply the honest time to read a value the user supplied at navigation.
    this.deepLinkedProject = initialDeepLinkProject() ?? this.deepLinkedProjectName();

    // THE ONLY consumer of the query string.
    //
    // There used to be a second one here: an `openProjects=true` handler that
    // opened the picker and then ran navigate(['/'], {replaceUrl:true}) 500 ms
    // later. That rewrote the URL and DESTROYED a ?project= followed inside the
    // window, so the deep link lost before it was ever read. Gating it was not
    // enough — two handlers racing over one query string is the defect, not the
    // order they happen to run in, so it is gone.
    //
    // Nothing is lost: restoreProjectOrShowPicker already routes to the picker when
    // no project is persisted, which is what openProjects existed to do. The one
    // in-app caller now opens the modal directly (see onCustomerChanged), and a
    // stale ?openProjects=true in a bookmark is simply ignored.
    this.activatedRoute.queryParams.subscribe(params => {
      const linked =
        readProjectParam(params) ??
        this.deepLinkedProjectName() ??
        this.deepLinkedProject;
      if (linked) {
        this.deepLinkedProject = linked;
      }
      if (linked) {
        this.openProjectFromDeepLink({ [PROJECT_QUERY_PARAM]: linked });
      }
    });

    // WAIT FOR MSAL ONCE. take(1) is the whole fix for the duplicate open.
    //
    // InteractionStatus returns to None after initialisation, after redirect
    // handling, and after EVERY silent token acquisition. Without take(1) this
    // re-ran setupAuthentication() each time — and the account handler's own
    // loadProjectList() call triggers a token acquisition, so the app scheduled
    // its second project open ~200 ms after the first, before the first 1000 ms
    // restore timer had even fired. Two timers, two identical opens, and the
    // gap between them was token timing rather than anything in this code, which
    // is why the duplicate looked non-deterministic on the wire.
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        take(1),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        console.log('Main Layout > MSAL initialization complete');
        this.setupAuthentication();
      });
  }

  /**
   * THE FOUR LATCHES ARE GONE.
   *
   * historicalIssuedFor, historicalInFlight, zoneIssuedFor and lossIssuedFor all
   * existed to recognise a real backfill among the phantom emissions a
   * BehaviorSubject cascade produces. HistoricalLoadService removes the cascade,
   * so there is one trigger, and one latch — inside that service, where the
   * question "has this selection loaded?" now has exactly one answer.
   */

  /** Set once the auth wiring has been built; see setupAuthentication(). */
  private authSetupDone = false;

  private setupAuthentication(): void {
    // ONCE PER SESSION. take(1) upstream already guarantees this today, but the
    // guard states the invariant where it is owned rather than leaving it to a
    // caller. Every subscription below is created fresh on each call, and the
    // distinctUntilChanged on the account stream is PER SUBSCRIPTION — a second
    // subscription starts with no history, immediately replays the current
    // account and re-runs the entire handler. That is what made 933's flattening
    // insufficient: it deduped inside the block that was being recreated.
    if (this.authSetupDone) {
      console.log('Main Layout > setupAuthentication already done; skipping');
      return;
    }
    this.authSetupDone = true;

    // Subscribe to role observables for Admin entry-point visibility
    this.authService.isAdmin$.subscribe((admin) => (this.isAdmin = admin));
    this.authService.isSuperAdmin$.subscribe((sa) => (this.isSuperAdmin = sa));
    this.authService.activeCustomer$.subscribe((c) => (this.activeCustomerName = c?.CustomerName ?? ''));

    this.authStatusService.status$
      .pipe(takeUntil(this._destroying$))
      .subscribe((status) => (this.authStatus = status));

    // The load stream is the ONE signal that the project list changed, so it is
    // where the grid's search index and model are rebuilt. Rebuilding anywhere else
    // is how the old pane ended up recomputing during change detection instead.
    // The transport follows the route, and the FIRST value matters as much as the
    // updates: on a cold load straight to the grid there is no NavigationEnd to wait
    // for, so it is seeded here rather than left false-then-corrected (or, worse,
    // true-then-corrected, which is a visible flash of chrome that does not belong).
    this.onProjectTab = isProjectTabUrl(this._router.url);
    this._router.events
      .pipe(takeUntil(this._destroying$))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.onProjectTab = isProjectTabUrl(event.urlAfterRedirects);
          // A TAB CLICK LEAVES THE REPORT PANE. The panel is not a route — it
          // renders over a hidden router-outlet — so navigating away used to
          // change the outlet underneath while the panel stayed on top, and the
          // only way out was the panel's own Back button. That is modal
          // behaviour from something that looks like a tab.
          //
          // Keyed off NavigationEnd rather than added to each tab's click
          // handler, so it covers every route into and out of the shell —
          // including the browser Back button and any tab added later. And
          // because it fires only on a SUCCESSFUL navigation, the tab gating
          // still holds: a click on a project-required tab with no project
          // selected does not navigate, so the pane correctly stays put.
          this.showExportPanel = false;
        }
      });

    this.projectService.projectListLoad$
      .pipe(takeUntil(this._destroying$))
      .subscribe((load) => (this.projectLoad = load));

    // SELECTION CAN NOW HAPPEN OFF-SCREEN. The picker is a route of its own, so the
    // shell learns about a choice through the service rather than from its own
    // click handler. onProjectChange stays the single place that loads a project —
    // it is idempotent on an unchanged name, and distinctUntilChanged keeps its own
    // write-back from bouncing straight back through here.
    // THE SELECTION SCREEN NOW SENDS A FILE, not a request for a picker.
    //
    // It used to send "open the dialog", and this opened the project modal on its
    // DEFAULT tab — 'existing' — so clicking "Upload a project" put the project
    // picker on top of the project picker. Nothing about that pane was the
    // uploader, which is why it read as blank.
    //
    // The pipeline stays here because an upload in progress must survive a route
    // change; only the file selection moved out, to the browser's own dialog.
    this.uploadDialog.upload$
      .pipe(takeUntil(this._destroying$))
      .subscribe((file) => this.beginUploadWithFile(file));

    this.projectService.selectedProject
      .pipe(distinctUntilChanged(), takeUntil(this._destroying$))
      .subscribe((name) => {
        if (name && name !== this.selectedProject) {
          this.onProjectChange(name);
        }
      });

    // ONE subscription per concern, none nested.
    //
    // This was isAuthenticated$ -> activeAccount$, nested and unguarded — the
    // same defect as LoginComponent's. MSAL emits both repeatedly while it
    // initialises, so the inner subscription was recreated on each outer
    // emission and every one of them ran the whole block: preferences.load(),
    // startPolling(), loadProjectList(), initializeProjects() and
    // restoreProjectOrShowPicker(). That is the remaining double-init —
    // GetProjectlist twice, preferences twice, the historical cascade twice, and
    // startPolling() stacking an extra alerts poller each time, which is why
    // summary was worse than 2x.
    //
    // initializeProjects() already carried a comment about being called from
    // "a nested subscription that re-emits"; it guarded its own timers and left
    // the nesting in place. This removes the nesting.
    this.authService.isAuthenticated$
      .pipe(takeUntil(this._destroying$))
      .subscribe((isAuthenticated) => {
        this.isAuthenticated = isAuthenticated;
        if (!isAuthenticated) {
          this.activeAccount = null;
          this.alertsService.stopPolling();
        }
      });

    // Session-scoped starts: once per authenticated session, not per emission.
    this.authService.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        take(1),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.preferencesService.load();
        this.alertsService.startPolling();
      });

    // Account-scoped work: once per DISTINCT account. An empty id means there is
    // no MSAL account, and MSAL is the only authority — nothing loads in that
    // case. There is deliberately no localStorage-derived fallback identity.
    this.authService.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        switchMap(() => this.authService.activeAccount$),
        distinctUntilChanged(
          (a, b) => (a?.localAccountId ?? '') === (b?.localAccountId ?? '')
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((account) => {
          console.log(
            `AUTH-ACCOUNT-HANDLER ${account?.localAccountId ?? '(no msal account)'}`
          );
          this.activeAccount = account;
          
          if (this.activeAccount?.localAccountId) {
            // MSAL authentication - use MSAL account data
            const CustomerTenantId = this.authService.getCurrentCustomerTenantId();
            console.log('Main Layout > Customer Tenant ID:', CustomerTenantId);
            
            // Pass the customer tenant ID to the project service
            this.projectService.loadProjectList(this.activeAccount.localAccountId, CustomerTenantId);
            this.initializeProjects();
            
            // Check and restore last selected project after project list loads
            this.restoreProjectOrShowPicker();
            this.startProjectListFreshness();
          }
      });
  }

  /**
   * Keep the project list fresh for a tab nobody is touching.
   *
   * THE BUG THIS EXISTS FOR: liveness is server-decided and arrives on the
   * GetProjectlist row as `isLive`. That one boolean gates the live socket, the
   * realtime fallback poll and the ONLINE pill, and the list was FETCH-ONCE — so
   * a cure that went live while the page was open was never noticed. Measured on
   * 2026-09-17: two list fetches in 45 minutes, and a cure that went live between
   * them showed as Historical, with no pill and one tick of telemetry, until the
   * tab was reloaded.
   *
   * TWO HALVES, DELIBERATELY:
   *
   *   PUSH  — the server already announces `projectChanged` to the caller's
   *           customer group and has done for some time; the client half was
   *           flag-gated off, so it was talking to nobody. This is the latency.
   *
   *   POLL  — the guarantee, and the reason the push is not enough on its own. A
   *           dropped socket, a refused subscribe or a disabled flag all fail
   *           SILENTLY and look exactly like "nothing changed". The poll is the
   *           floor that does not depend on a socket being healthy.
   *
   * WHY IT LIVES HERE AND NOT ON THE SELECTION SCREEN. It used to live on
   * ProjectSelectionComponent, which is a ROUTED component: it is destroyed the
   * moment the user navigates to /temperature, which is where they spend a cure.
   * Flipping the flag alone would have left exactly the reported case — a user
   * watching a project — no better off. MainLayoutComponent is the shell that
   * outlives every project tab, and it already owns the realtime poll timer, so
   * the two cadences sit together.
   *
   * START ONCE. The auth handler re-emits, and this method is called from it;
   * without the guard each emission would add another socket and another timer,
   * which is precisely how this component once accumulated ~50 stacked pollers.
   */
  private startProjectListFreshness(): void {
    if (this.listFreshnessStarted) {
      return;
    }
    this.listFreshnessStarted = true;

    // THE SUBSCRIPTION IS NOT CONDITIONAL ON HAVING A LIVE CURE, AND MUST NOT BECOME SO.
    //
    // This listens on the CUSTOMER group, `c-<customerId>`, not on a project group.
    // A tab sitting on a FINISHED cure still needs it: PROJECT_ANNOUNCED is how it
    // learns that a cure STARTED somewhere else in the tenant. Scoping this to "tabs
    // with a live project selected" would reintroduce the original defect — a tab
    // that can never learn about the thing it is waiting for — in a new place.
    //
    // WHAT RIDES `c-<customerId>` TODAY, enumerated rather than assumed, because the
    // next person to read this will be deciding whether the subscription can go:
    //   - `projectChanged` (PROJECT_ANNOUNCED) — and NOTHING ELSE. The hub has exactly
    //     two targets; the other is `tick`, which goes to the per-project group.
    // ALERTS DO NOT RIDE THIS GROUP TODAY. They are a separate HTTP path —
    // `v1/alerts`, `v1/alerts/summary`, a server-side TimerTrigger — and AlertsService
    // runs its own 60 s poll for the bell. Unsubscribing here would NOT silence them.
    // That is stated explicitly because the convenient version ("it also carries
    // alerts") is currently false, and a comment someone relies on must not be.
    // IF ALERTS EVER MOVE ONTO THIS GROUP, unsubscribing starts silencing them, and
    // this comment needs to say so rather than being quietly outgrown.
    this.projectListPush.changed$
      .pipe(takeUntil(this._destroying$))
      .subscribe((message) => {
        console.log(
          `PROJECT-LIST-PUSH refetching (${message?.reason ?? 'projectChanged'})`
        );
        this.refetchProjectListQuietly();
      });

    // CONNECTION STATE, SURFACED. The project list's freshness depends on this socket,
    // and a socket that dies quietly is indistinguishable from a tenant where nothing
    // has happened for two hours. beta-dev ran a FOURTEEN DAY silent SignalR outage
    // that nobody noticed, which is precisely the failure this exists to make visible.
    this.projectListPush.state$
      .pipe(takeUntil(this._destroying$))
      .subscribe((state) => (this.pushState = state));

    this.reconcileOnReconnect();

    // THE FLOOR. Runs alongside the push and the reconnect reconcile, never instead
    // of them. First tick is one INTERVAL away, not immediate: the initial load has
    // just been issued by the caller and an immediate second fetch is a pure
    // duplicate.
    const refreshMs = projectListRefreshMs();
    if (refreshMs > 0) {
      this.projectListRefreshSub = timer(refreshMs, refreshMs)
        .pipe(takeUntil(this._destroying$))
        .subscribe(() => this.refetchProjectListQuietly());
    }

    void this.projectListPush.start();
  }

  /**
   * RECONCILE ON CONNECT AND ON EVERY RECONNECT — the one gap the push cannot close
   * by itself, and the reason this is not a timer.
   *
   * SignalR DOES NOT BUFFER ACROSS A DROP. Anything the server announced while the
   * socket was down was delivered to nobody, and nothing replays it. A cure that
   * started — or finished — in that window would otherwise never be noticed, because
   * there is no longer a poll behind this to catch it.
   *
   * EVENT-DRIVEN, NOT CLOCK-DRIVEN. It runs precisely when the push was NOT
   * delivering, so it cannot race the push: the two are mutually exclusive by
   * construction. That is the property a timer does not have — a timer fires while
   * the socket is healthy, which is the duplicate work we just deleted, and it
   * fires on a clock rather than on evidence that something was missed.
   *
   * ONE REFETCH PER CONNECTION LIFETIME, so the steady-state cost is zero. This
   * replaces 80 requests per tab per hour.
   *
   * The FIRST 'live' is included deliberately: the caller's initial load and the
   * socket opening are not ordered with respect to each other, so a project that
   * changed between them would otherwise be missed on exactly the load where the
   * user is most likely to be waiting for it.
   */
  private reconcileOnReconnect(): void {
    this.projectListPush.state$
      .pipe(
        // ORDER IS LOAD-BEARING: distinct FIRST, then filter.
        //
        // Filtering to 'live' before de-duping collapses live -> failed -> live into
        // a single emission, because the intermediate states never reach
        // distinctUntilChanged — so a RECONNECT would reconcile nothing, which is the
        // one case this exists for. De-duping the RAW state first means each return
        // to 'live' is a fresh emission, while a repeated 'live' still suppresses.
        distinctUntilChanged(),
        filter((state) => state === 'live'),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        console.log('PROJECT-LIST-PUSH reconciling after connect/reconnect');
        this.refetchProjectListQuietly();
      });
  }

  /**
   * Re-ask for the list without disturbing anything.
   *
   * QUIET is what makes this safe to run in the background: no spinner, no
   * blanking of a populated grid when a refresh blips, and — since the signature
   * check in ProjectService — no emission at all when the answer is unchanged. A
   * refetch that finds nothing new is invisible to every subscriber.
   *
   * It updates the LIVENESS CLASSIFICATION and nothing else. It does not reload a
   * project, touch the telemetry store, or re-run the historical cascade.
   */
  private refetchProjectListQuietly(): void {
    const objectId =
      this.activeAccount?.localAccountId ?? this.authService.getObjectId();
    if (!objectId) {
      return;
    }
    this.projectService.loadProjectList(
      objectId,
      this.authService.getCurrentCustomerTenantId(),
      true
    );
  }

  initializeProjects() {
    this.isRunning = false;

    // START ONCE. These two timers were created on every call and never torn
    // down, and this method runs from the auth flow — inside a nested
    // subscription that re-emits. Each emission stacked another 5 s poll, so the
    // realtime fetch rate became a function of how many times the account had
    // emitted: ~50 stacked timers is ten requests a second, every one carrying
    // the same cursor because a completed project has nothing newer.
    //
    // The guard matters more than the arithmetic: a poll loop whose rate depends
    // on an unrelated subscription is unbounded by construction.
    if (this.pollTimersStarted) {
      return;
    }
    this.pollTimersStarted = true;
    MainLayoutComponent.pollerCount++;
    console.trace(
      `RT-POLLER setup #${MainLayoutComponent.pollerCount} (component instance #${this.instanceId})`
    );

    this.playbackTimerSub = timer(0, 100).subscribe((ellapsedCycles) => {
      //console.log('timer');
      if (this.isRunning) {
        //console.log('app comp > time is running');

        if (this.tick < this.tickMax) {
          this.tick += 1;
          var strTick = this.tick.toString();
          this.updateChartDataForTick(strTick);
        } else {
          this.isRunning = false;
        }
      }
    });

    this.realtimePollSub = timer(0, 5 * 1000).subscribe((ellapsedCycles) => {
      // DEVICE RECENCY, EVALUATED UNCONDITIONALLY.
      //
      // Deliberately above every gate below. This timer always ticks; only the
      // FETCH inside it is gated, and while the socket is live that gate is shut
      // by design. The old detector lived inside the fetch's response handlers,
      // so a healthy socket silenced it and 'offline' became unreachable — the
      // exact dependency on transport that this line exists to break.
      this.projectService.evaluateDeviceRecency();
      // EVERY TERM OF THE GATE, and which one is holding it shut.
      //
      // The block below is guarded by six conditions and the log carried four of
      // them, so a closed gate could not be attributed without reading the source
      // and guessing. It cost a verification round: loss stopped appending, and
      // the two missing terms — the socket's live flag and the project's own
      // isLive — were the only ones that could not be read from a capture.
      const gate = {
        selectedProject: this.selectedProject,
        fetchRealTimeData: this.fetchRealTimeData,
        projectIsLive: this.selectedProjectIsLive(),
        socketState: this.liveTelemetry.state,
        socketIsLive: this.liveTelemetry.isLive,
        showProjectModal: this.showProjectModal,
        suspendRealtime: this.projectService.suspendRealtime,
        tempDataLoading: this.tempDataLoading,
        zoneDataLoading: this.zoneDataLoading,
        lossDataLoading: this.lossDataLoading,
      };
      const blockedBy = [
        !this.selectedProject && 'no project',
        !this.fetchRealTimeData && 'fetchRealTimeData=false (loss backfill has not landed)',
        !this.selectedProjectIsLive() && 'project is not live in the list',
        this.showProjectModal && 'project modal open',
        this.projectService.suspendRealtime && 'suspendRealtime (export)',
        this.liveTelemetry.isLive && 'socket is live — poll suppressed BY DESIGN',
        this.tempDataLoading && 'tempDataLoading',
        this.zoneDataLoading && 'zoneDataLoading',
        this.lossDataLoading && 'lossDataLoading',
      ].filter(Boolean);
      console.log(
        `RT-GATE ${blockedBy.length ? 'BLOCKED: ' + blockedBy.join(' | ') : 'OPEN'}`,
        { ...gate, cursors: this.projectService.cursors.snapshot(), ellapsedCycles }
      );
      console.log('app comp > Fetching Realtime Data...', gate);

      // Item 4: while the live socket is genuinely connected the stream supplies
      // ticks, so the temperature poll would only duplicate work. The instant it
      // is off, failed or dropped, isLive goes false and polling resumes on the
      // very next 5 s cycle — polling stays the source of truth.
      // NOTE: deliberately NOT reusing projectService.suspendRealtime, which the
      // PDF export flow owns; overloading it would let one cancel the other.
      // PUSH FIRST. If the project is live but the socket is not up — usually
      // because the project list had not arrived when it was opened — connect
      // now. start() is idempotent for the same project, so this is a re-check
      // rather than a reconnect, and it is what keeps polling a fallback rather
      // than the primary path.
      // `isLive` is false while CONNECTING and while RECONNECTING, so keying the
      // re-check on it would ask for another connect every tick during a slow
      // negotiate — the poll storm's shape, applied to sockets. Only a settled
      // not-connected state should trigger a connect.
      const socketState = this.liveTelemetry.state;
      const socketIdle = socketState === 'disconnected' || socketState === 'disabled';
      if (this.selectedProjectIsLive() && socketIdle) {
        void this.liveTelemetry.start(this.selectedProject, {});
      }

      const liveActive = this.liveTelemetry.isLive;
      if (this.selectedProject.length > 0 && this.realtimeWanted() && !this.showProjectModal && !this.projectService.suspendRealtime && !liveActive && !this.tempDataLoading && !this.zoneDataLoading && !this.lossDataLoading) {
        // Fetch Temp Data
        this.projectService
          .loadTemperatureData(
            this.selectedProject,
            true,
            this.projectService.getCursor('temperature')
          )
          .subscribe(
            (data) => {
              if (data.TRC.length > 0) {
                console.log('app comp > Realtime response received');
                this.projectService.setDeviceOnline();
                this.projectService.setRTTempData(data);
                // Prefer the server high-water mark (item 2); fall back to the
                // largest row epoch when the API build predates HWM. Never
                // regresses, so the cursor cannot replay ingested ticks.
                this.lastEpocTime = this.projectService.advanceCursor(
                  'temperature',
                  data,
                  maxEpochOf(data)
                );

                // Take the readout from the NEWEST row, and advance the tick
                // counter by however many rows arrived — the chart components
                // ingest all of them, so a fixed +1 would drift behind whenever a
                // poll interval spanned more than one tick.
                const newest = newestRowOf(data);
                if (newest) {
                  this.currTime = newest.T;
                }
                console.log(
                  'Epoc Time: ' + this.lastEpocTime + ' = ' + this.currTime
                );
                this.tick += data.TRC.length;
                //this.projTemperatureData = data;
                this.currDuration = this.projTemperatureData.MI * this.tick;
              } else {
                console.log('app comp > No Realtime data');
                this.projectService.checkDeviceTimeout();
              }
              
              // Extract and set notes data from real-time temperature data response
              if (data.N) {
                this.projectService.setNotesData(data.N);
              }
              // Extract and set markers data from real-time temperature data response
              if (data.MAR) {
                console.log('MainLayout > Loading markers from real-time temperature data:', data.MAR);
                this.projectService.loadMarkers(data.MAR);
              }
            },
            (error) => {
              console.log(error);
              this.projectService.checkDeviceTimeout();
            }
          );

          // Notes don't need continuous real-time updates like temperature data
          // They will be loaded when project is selected and when notes are saved
          // Fetch Zone Data
        console.log('app comp > Fetching Zone RT Data...');
        this.projectService.loadZoneDataRT(this.selectedProject);

        // Fetch Loss Data
        console.log('app comp > Fetching Loss RT Data...');
        this.projectService.loadLossRTData(this.selectedProject);
      }
    });

    // ONE DERIVATION FOR THE SLIDER RANGE AND THE ELAPSED READOUT (H8).
    //
    // THE DEFECT: on a LIVE cure the chart advanced while `tickMax` and
    // `currDuration` stayed pinned to the page-load payload. Measured on the real
    // broadcast path, unmodified main, 5 live ticks:
    //
    //     store rows   557 -> 562      store last UT  +5 min
    //     ml.tick      556 -> 561
    //     tickMax      556 -> 556      PINNED
    //     currDuration 33,975 -> 33,975  PINNED
    //
    // Both values were written ONLY inside the historical `temperatureData`
    // subscription below, so a reload fixed them and nothing else did.
    //
    // WHY HERE. There are THREE live routes — the SignalR broadcast
    // (`ingestFamily`), the catch-up poll, and the realtime poll — and all three
    // already call `setRTTempData`, which emits here. Deriving at the funnel means
    // a fourth route cannot forget, which is the same reasoning as
    // ChartTickService: registration IS the wiring.
    //
    // NOT by re-enabling `//this.projTemperatureData = data` in the poll handler.
    //
    // ARCHAEOLOGY, CORRECTED. Origin is `2a9637f` ("Time calculation added",
    // 2025-03-22, sp-cloudiotservice) in `src/app/app.component.ts` — NOT 3ef27d4,
    // which is only the rename of app.component.ts into this file and carried the
    // line across. Verified with
    //   git log --all --follow -G '^[[:space:]]*//[[:space:]]*this\.projTemperatureData' \
    //     -- src/app/main-layout/main-layout.component.ts
    // which returns 2a9637f alone.
    //
    // In that commit the line arrives ALREADY COMMENTED, in the SAME hunk that
    // adds `this.currDuration = this.projTemperatureData.MI * this.tick;`. So the
    // disabled correct source and the inferior fallback that replaced it were
    // written together. It was never live here, so there is no regression to
    // reintroduce.
    //
    // (The live `this.projTemperatureData = data` that turns up in other commits
    // is a DIFFERENT occurrence — the HISTORICAL handler's assignment, live since
    // 2dc733b (2023-07-10) and still present below.)
    //
    // And enabling it would BREAK this fix regardless of its history: an RT
    // payload's TRC holds only the NEW rows, so assigning it would discard TRC[0],
    // the cure's first row and the thing `last.UT - first.UT` measures from. That
    // reasoning rests on the code, not on the archaeology.
    //
    // REPLAY-SAFE. A re-broadcast or an overlapping catch-up poll can deliver rows
    // already counted, so only rows strictly newer than the newest UT seen are
    // counted. That makes the count converge rather than drift, which matters
    // because two of the three routes can be in flight at once.
    this.projectService.temperatureRTData.subscribe((data) =>
      this.deriveLiveRange(data)
    );

    this.projectService.temperatureData.subscribe((data) => {
      this.tempDataLoading = false;
      this.projTemperatureData = data;
      this.projectLoadPerc = 30;

      if (data == null) {
        console.log('Error in App Comp: Temp data is null.');
        return;
      } else if (data.TRC == null) {
        console.log('Error in App Comp: Temp data > TRC is null.');
        return;
      }
      // NOTHING TO RENDER FROM, WHICH IS NO LONGER THE SAME AS NOTHING TO LOAD.
      //
      // temperatureData is a BehaviorSubject seeded with TRC: [], so subscribing
      // replays that seed. This guard used to carry a second, much heavier job:
      // stopping the replayed seed from running the zone -> loss cascade. It could
      // not distinguish the seed from a REAL backfill that is genuinely empty, so
      // a project with no rows never loaded its other two families at all.
      //
      // The cascade is gone, so this is only what it says: an empty payload has no
      // tick range, no time base and no rows to draw, and the derivations below
      // would produce tickMax = -1 and a 1899 date from it. Whether the project is
      // genuinely empty is now answered by HistoricalLoadService, on the load
      // result, and shown to the user.
      if (data.TRC.length === 0) {
        return;
      }
      this.tickMax = data.TRC.length - 1;
      this.tick = this.tickMax;
      // RE-BASE THE LIVE COUNT. The backfill is the authoritative set at this
      // moment, so the live tally starts again from it — and the high-water UT is
      // seeded from the newest backfill row, or the first live batch would count
      // rows this payload already contains.
      this.liveTickCount = 0;
      this.newestLiveUT = data.TRC[data.TRC.length - 1]?.UT ?? 0;

      // SEED THE TIME BASE from the backfill.
      //
      // currTime was only ever set by the realtime poll, the live-tick stream and
      // the slider — never by the historical load. So a project opened and left
      // alone showed its initialiser, new Date(0, 0, 0), which renders as
      // "12/31/99, 12:00:00 AM" in every chart footer. The data always had real
      // timestamps; nothing was reading them.
      // UT is the reliable field: measured as real 13-digit epoch MILLISECONDS
      // (e.g. 1787289855386). `T` is typed as a Date but arrives as whatever the
      // serialiser produced, so it is only a fallback.
      const newestRow = data.TRC[this.tickMax];
      const firstRow = data.TRC[0];
      if (typeof newestRow?.UT === 'number' && newestRow.UT > 0) {
        this.currTime = new Date(newestRow.UT);
      } else if (newestRow?.T) {
        this.currTime = newestRow.T;
      }

      // Elapsed from the cure's OWN start, measured from the timestamps rather
      // than inferred as MI * tick. The two agree only while every interval is
      // exactly MI; a gap in the record made the inferred figure drift, and it
      // read 0 whenever MI was absent.
      if (
        typeof firstRow?.UT === 'number' &&
        typeof newestRow?.UT === 'number' &&
        newestRow.UT >= firstRow.UT
      ) {
        this.currDuration = Math.round((newestRow.UT - firstRow.UT) / 1000);
      } else {
        this.currDuration = (data.MI ?? 0) * this.tick;
      }

      // NO FETCH IS TRIGGERED FROM HERE ANY MORE.
      //
      // This handler used to fan out into a zone read, whose arrival fanned out
      // into a loss read. That made the trigger for two thirds of the historical
      // load "an emission on a BehaviorSubject" — which a replayed seed, a
      // re-subscribe and a real backfill all satisfy identically. The latch that
      // stood here was an attempt to tell those apart after the fact, and could
      // not, because they are indistinguishable by construction.
      //
      // HistoricalLoadService asks for all three families in one awaited
      // sequence. This subscription now does only what a render channel should:
      // update what is on screen from the rows it was handed.
    });

    this.projectService.zoneData.subscribe(() => {
      // Render channel only — the loss read that used to hang off this arrival is
      // now issued alongside zone, in parallel, by HistoricalLoadService.
      this.tempDataLoading = false;
      this.zoneDataLoading = false;
      this.projectLoadPerc = 60;
    });

    this.projectService.lossData.subscribe((data) => {
      // Retained so the next backfill can size the loss window (mirror-aware).
      this.projLossData = data;
      this.lossDataLoading = false;
      var validLossData = true;
      this.projectLoadPerc = 90;

      if (data == null || data === undefined) {
        validLossData = false;
      } else if (data.LRC == null) {
        validLossData = false;
      } else if (data.LRC.length == 0) {
        validLossData = false;
      }
      //this.setTickToEnd();
      this.fetchRealTimeData = true;

      if (validLossData) {
        const lossDataRows = data.LRC;
        var tickCount = data.LRC.length - 1;
        // Update Faults
        if (lossDataRows[tickCount].FD.length > 0) {
          var FD = lossDataRows[tickCount].FD;
          if (FD.some((c) => c.FT == '1')) {
            hasCMSFaults = true;
            console.log('has CMS faults 1');
            this.projectService.setFiberFaultLevel(1);
          } else if (FD.some((c) => c.FT == '2')) {
            hasCMSFaults = true;
            console.log('has CMS faults 2');
            this.projectService.setFiberFaultLevel(2);
          }
        }
      }

      if (this.selectedProject.length > 0 || this.uploadedroject.length > 0) {
        //this.notesDataLoading = true;
        //console.log('tempData subscription handler. loading data for zone for proj: ' + this.selectedProject);
        // Notes are now loaded from temperature data response (N property)
        // No need for separate notes API calls
        this.projectLoadPerc = 100;
      }

      var hasCMSFaults = false;
    });

    // uploadedProject is a BehaviorSubject seeded with '' , so subscribing
    // REPLAYS that empty value immediately — and this handler turned it straight
    // into onProjectChange(''). That was the first of the two PROJECT-CHANGE
    // lines in the capture: an empty open, followed by the real deep-link one,
    // each running the project-open path.
    //
    // An empty name is never a real upload, so it is filtered rather than
    // guarded downstream: the subject's seed is not an event.
    this.uploadedProjectSubscription =
      this.projectService.uploadedProject
        .pipe(filter((projName) => !!projName))
        .subscribe(
          (projName) => {
            this.onProjectChange(projName, true);
            this.projectLoadPerc = 10;
            console.log('app comp > loading Temp data');
          },
          (error) => console.log(error)
        );

    this.projectService.inFlightTelemetry
      .pipe(takeUntil(this._destroying$))
      .subscribe((count) => (this.telemetryInFlight = count > 0));

    this.fiberFaultLevelSubscription =
      this.projectService.fiberFaultLevel.subscribe((level) => {
        this.fiberFaultLevel = level;
      });

    this.cmsFaultLevelSubscription =
      this.projectService.cmsFaultLevel.subscribe((level) => {
        this.cmsFaultLevel = level;
      });

    this.projectService.deviceStatus$.subscribe((status) => {
      this.deviceStatus = status;
    });
  }

  toggleTimer() {
    this.isRunning = !this.isRunning;
  }

  /**
   * Re-derive the slider range and the elapsed readout from a live payload.
   *
   * EXTRACTED SO IT CAN BE DRIVEN DIRECTLY. Inlined in the subscription, the only
   * way to test it was to copy the body into a spec — and a spec that re-implements
   * the code it covers passes whatever the code does. See the full rationale on the
   * subscription above.
   */
  private deriveLiveRange(data: ProjectTemperatureData | null | undefined): void {
    const rows = data?.TRC;
    if (!rows || rows.length === 0) {
      return;
    }
    const fresh = rows.filter(
      (r) => typeof r?.UT === 'number' && r.UT > this.newestLiveUT
    );
    if (fresh.length === 0) {
      return;
    }
    this.newestLiveUT = fresh[fresh.length - 1].UT;
    this.liveTickCount += fresh.length;

    const backfillRows = this.projTemperatureData?.TRC?.length ?? 0;
    if (backfillRows === 0) {
      return;
    }
    this.tickMax = backfillRows - 1 + this.liveTickCount;

    const firstUT = this.projTemperatureData?.TRC?.[0]?.UT;
    if (typeof firstUT === 'number' && this.newestLiveUT >= firstUT) {
      this.currDuration = Math.round((this.newestLiveUT - firstUT) / 1000);
    }

    if (this.tick > this.tickMax) {
      this.tick = this.tickMax;
    }
  }

  updateChartDataForTick(val: string) {
    const sliderVal: number = +val;
    this.tick = sliderVal;
    //console.log('sliderVal: ' + sliderVal);
    if (this.projTemperatureData) {
      if (this.projTemperatureData.TRC.length > 0) {
        // Same source as the backfill seeding above — UT first, so scrubbing
        // does not fall back to a different notion of time than the load did.
        const row = this.projTemperatureData.TRC[sliderVal];
        const first = this.projTemperatureData.TRC[0];
        this.currTime =
          typeof row?.UT === 'number' && row.UT > 0 ? new Date(row.UT) : row?.T;
        this.currDuration =
          typeof row?.UT === 'number' && typeof first?.UT === 'number'
            ? Math.round((row.UT - first.UT) / 1000)
            : this.projTemperatureData.MI * this.tick;
        this.projectService.setTick(sliderVal);
      }
    }
  }

  setTickToBeginning() {
    this.tick = 0;
    this.updateChartDataForTick('0');
  }

  setTickToEnd() {
    this.tick = this.tickMax;
    this.updateChartDataForTick(this.tickMax.toString());
  }

  /**
   * Navigating to /health is not a reason to fetch loss.
   *
   * This was a SECOND, INDEPENDENT initiator of the loss backfill, behind its own
   * latch (`lossDataLoaded`) which knew nothing about the cascade's `lossIssuedFor`
   * — two latches, two answers to "is loss loaded?", and a duplicate whenever they
   * disagreed. Loss is part of the project open now, so by the time this tab can
   * be reached the rows are already held or already known to be empty.
   */
  showHealthPage() {
    this._router.navigateByUrl('health');
  }


  showFiberFaultColorClass(): string {
    // Offline (at the scrubbed tick) takes precedence — solid red sidebar.
    if (this.isOfflineAtTick) {
      return 'leftnav offline';
    }
    if (this.fiberFaultLevel == 2 || this.cmsFaultLevel == 2) {
      return 'leftnav fiber-error';
    } else if (this.fiberFaultLevel == 1 || this.cmsFaultLevel == 1) {
      return 'leftnav fiber-warn';
    } else {
      return 'leftnav';
    }
  }



  login(): void {
    this.authService.login();
  }

  /**
   * Sign in as somebody else.
   *
   * Deliberately the same call as a first sign-in: login() forgets the recorded
   * choice and asks Microsoft with prompt=select_account, so the picker appears
   * and whatever the user picks becomes the new anchor. Anything cleverer —
   * silently switching to another cached account — would re-create the bug where
   * the app decides which identity you are.
   */
  switchAccount(): void {
    this.userDropdownOpen = false;
    this.authService.login();
  }

  /** True while the session cannot actually make authenticated calls. */
  get authDegraded(): boolean {
    return this.authStatus.kind !== 'signed-in';
  }

  /** Short label for the status chip. */
  get authStatusLabel(): string {
    switch (this.authStatus.kind) {
      case 'signed-in':
        return 'Signed in';
      case 'token-refresh-failed':
        return 'Session expired';
      case 'account-ambiguous':
        return 'Choose an account';
      default:
        return 'Signed out';
    }
  }

  /**
   * Which tenant the signed-in identity belongs to.
   *
   * Distinct from activeCustomerName: that is the CUSTOMER whose data is being
   * viewed, which an admin can switch. This is who you are signed in AS, and
   * conflating the two is how someone ends up sure they are one and actually the
   * other.
   */
  get authTenant(): string {
    return this.authStatus.tenant;
  }

  logout(): void {
    this.authService.logout();
  }

  // Modal methods
  // ── Export panel methods ─────────────────────────────────────────────────

  toggleExportPanel(): void {
    this.showExportPanel = !this.showExportPanel;
    if (this.showExportPanel) {
      this.exportPanelMode = 'pdf';
    }
  }

  setExportMode(mode: 'pdf' | 'csv'): void {
    this.exportPanelMode = mode;
  }

  closeExportPanel(): void {
    this.showExportPanel = false;
  }

  getExportTenantId(): string {
    return this.authService.getCurrentCustomerTenantId() || '';
  }

  // ── Project modal methods ────────────────────────────────────────────────

  openProjectModal(): void {
    this.showProjectModal = true;
    this.activeTab = 'existing';
    this.isModalMaximized = true;
    this.isModalMinimized = false;
    this.resetModalSize();
    
    // If a project is currently selected, load its preferences into the modal controls
    if (this.selectedProject && this.projectService.hasProjectUnitPreference(this.selectedProject)) {
      const savedUnit = this.projectService.getProjectUnitPreference(this.selectedProject);
      const savedLang = this.projectService.getProjectLanguagePreference(this.selectedProject);
      
      // Sync both unit properties
      this.projectService.isMetricUnit = savedUnit;
      this.projectService.toUpload_isMetricUnit = savedUnit;
      this.projectService.selectedLang = savedLang;
      this.projectService.toUpload_selectedLang = savedLang;
      
      console.log('Modal opened - loaded current project preferences:', 
                  this.projectService.toUpload_isMetricUnit ? 'Metric' : 'Imperial');
    }
  }

  closeProjectModal(reason?: string): void {
    console.log('Modal closing. Reason:', reason || 'Unknown');
    this.showProjectModal = false;
    this.isModalMaximized = false;
    this.isModalMinimized = false;
    this.resetModalSize();
  }



  /**
   * THE grid model — filtered, ordered, sectioned and with every cell precomputed.
   *
   * Rebuilt when the DATA or the CRITERIA change, and at no other time. It used to
   * be rebuilt implicitly by change detection: getFilteredProjects() filtered and
   * sorted a fresh array on every call and the template reached it six or more
   * times per cycle, while getAllUserParameterNames() rescanned every project's
   * every parameter once per header cell AND once per body cell. Six UDF columns
   * over a hundred rows made six hundred full scans per cycle on a field iPad.
   */
  gridModel: ProjectGridModel = buildProjectGridModel([], DEFAULT_PROJECT_GRID_VIEW, EMPTY_CRITERIA);

  /**
   * The resolved saved view. Server-normalised: the three tiers are merged and made
   * coherent in DataServices, so there is one implementation of the cascade and this
   * is not it. Until preferences load, the built-in default stands.
   */
  gridView: ProjectGridView = DEFAULT_PROJECT_GRID_VIEW;

  /** Search text per project, rebuilt only when the PROJECT LIST changes. */
  private gridHaystacks = new Map<ProjectInfo, string>();

  /**
   * Called when the project list arrives or changes. Rebuilds the search index and
   * then the model — the only two things that depend on the data itself.
   */
  private onProjectsChanged(): void {
    const projects = this.projectService.projectList ?? [];
    this.gridHaystacks = new Map(projects.map((p) => [p, searchHaystack(p)]));
    this.rebuildGrid();
  }


  private rebuildGrid(): void {
    this.gridModel = buildProjectGridModel(
      this.projectService.projectList ?? [],
      this.gridView,
      {
        searchTerm: this.searchTerm,
        statusFilter: this.selectedFilter as StatusFilter,
        dateFrom: this.dateFilterFrom,
        dateTo: this.dateFilterTo,
      },
      this.gridHaystacks,
      // The grid renders dates in the user's chosen ORDER, resolved from the
      // cascade. Read at build time rather than held as state so a preference
      // change is picked up by the next rebuild without another subscription.
      normalizeDateFormat(this.preferencesService.effective$.value.dateFormat),
      normalizeTimeFormat(this.preferencesService.effective$.value.timeFormat)
    );
  }

  getUserDisplayName(): string {
    // For MSAL authentication, use the account username
    if (this.activeAccount?.username) {
      return this.activeAccount.username;
    }
    
    // Fallback to UserAuthService username
    return this.authService.UserName || 'User';
  }




  /**
   * Is a project open? Drives whether the project-dependent tabs are usable.
   *
   * Joel: "those tabs should not be available until a project is selected."
   * Temperature, Graph, Curing, Subviews, Notes, Health and Info all render a cure;
   * opened with nothing selected they show an empty shell that reads as a broken
   * app rather than as a choice not yet made.
   */
  /**
   * Is a chart on screen for the playback transport to control?
   *
   * The transport (prev / play / next and the time slider) lives in .centerarea
   * AFTER the router-outlet, so without this it renders under EVERY routed screen —
   * it was sitting at the bottom of the project selection grid, and under the admin
   * console, as a slider with no chart to scrub and a duration readout of nothing.
   *
   * Tracked from NavigationEnd rather than read as a getter in the template: a
   * getter would re-run on every change-detection cycle, which is the pattern this
   * screen was rebuilt to get away from.
   */
  onProjectTab = false;

  /**
   * IS A PROJECT OPEN? Asked of the SERVICE, not of this component's own field.
   *
   * Joel, on prod2: "I can't seem to navigate to any chart tab except
   * temperature. CLICKING A DIFFERENT TAB TAKES ME BACK TO TEMPERATURE." Every
   * tab is bound `[routerLink]="projectSelected ? 'graph' : null"`, and
   * onTabClick() preventDefaults the click when this is false — so when it
   * answers NO, clicking a tab does nothing at all. The user is already on
   * /temperature, so "nothing happened" and "it sent me back to temperature" are
   * the same picture. There is no navigation to debug; the link was never armed.
   *
   * This used to read `this.selectedProject`, a field written in exactly one
   * place: onProjectChange(). So any route by which a project becomes open
   * WITHOUT that method completing leaves the charts rendering — they read the
   * SERVICE — while every tab stays dead. Two answers to "is a project open?",
   * and the charts and the tab strip consulted different ones.
   *
   * ProjectService is the one the rest of the app already believes: it is what
   * the chart components subscribe to, what requests are built from, and what
   * survives this component's private bookkeeping. Asking it means the tab strip
   * cannot disagree with the screen behind it.
   *
   * A getter, and deliberately not NavigationEnd-tracked like onProjectTab: this
   * has to be right the instant the selection lands, and it reads one
   * BehaviorSubject value.
   */
  get projectSelected(): boolean {
    return !!this.selectedProject || !!this.projectService.selectedProjectName;
  }

  readonly noProjectReason = NO_PROJECT_TAB_REASON;

  /**
   * A disabled tab must not navigate.
   *
   * `[routerLink]="null"` alone is not enough: these are real <a href="#"> elements,
   * so a click still moves the browser to "#" and, more to the point, a future edit
   * that restores the routerLink would silently re-enable navigation with no test
   * noticing. The click is refused here as well, so the gate holds even if the
   * binding is wrong.
   */
  onTabClick(event: Event): boolean {
    if (!this.projectSelected) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    return true;
  }

  /**
   * Restore the last project on load, or send the user to the picker.
   *
   * WAITS FOR THE LIST TO SETTLE — never a timer. This replaces
   * restoreLastSelectedProject/applyRestoredProject wholesale rather than porting
   * them: the rule they encoded is now decideRestore(), which is pure, and the
   * "open the picker" branch is a NAVIGATION to the selection screen rather than a
   * modal over whatever was behind it.
   *
   * The discipline is theirs and is the whole point (5b0c1c2): the old flat
   * setTimeout(1000) lost the race by ~38 ms on nearly every reload and treated
   * "the list has not loaded yet" as "no project was chosen". A BehaviorSubject
   * replays its current value, so an already-loaded list still decides
   * immediately — waiting costs nothing and guessing cost a year of it looking
   * like a routing bug.
   */
  private restoreProjectOrShowPicker(): void {
    this.projectService.projectListLoad$
      .pipe(
        filter((load) => load.kind === 'loaded' || load.kind === 'failed'),
        take(1),
        // Bounded, so a load that never settles cannot leave the user with neither
        // content nor a picker.
        timeout(PROJECT_LIST_SETTLE_MS),
        catchError(() =>
          of<ProjectListLoad>({
            kind: 'failed',
            status: 0,
            message: 'The project list did not load in time.',
            detail: '',
          })
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((load) => {
        const decision = decideRestore({
          listState: load.kind,
          savedProjectName: localStorage.getItem(LAST_SELECTED_PROJECT_KEY),
          projectNames: this.projectService.projectList.map((p) => p.ProjectName),
          deepLinkPresent: !!(this.deepLinkedProject ?? this.deepLinkedProjectName()),
        });

        switch (decision.kind) {
          case 'deep-link':
            return;                       // that path owns the selection
          case 'wait':
            return;                       // unreachable: the filter above is the wait
          case 'restore':
            // fromRestore: automatic, so it must not move the user off a
            // non-project route such as /admin/devices.
            this.onProjectChange(decision.projectName, false, true);
            return;
          case 'show-picker':
            if (decision.clearSaved) localStorage.removeItem(LAST_SELECTED_PROJECT_KEY);
            // ONLY IF THE USER IS ON A PROJECT SCREEN. This branch fires on any
            // cold load with no project to restore, and it used to navigate
            // unconditionally — so opening /admin/users in a fresh tab loaded the
            // admin route, then the restore settled a beat later and threw the
            // user to /projects. The tab title read "Loading /admin/users" and
            // then the app bounced it, which is exactly what a deep link looked
            // like from the outside.
            //
            // The 'restore' case above already refuses to move someone off a
            // non-project route; this one had no such guard, which is the whole
            // bug. Same predicate, so the two branches cannot disagree.
            //
            // The admin screens do not need a project — that is the point of
            // them — and the picker is one click away in the nav for anyone who
            // does want one.
            if (!isProjectTabUrl(this._router.url) && this._router.url !== '/') return;
            this._router.navigate([SELECTION_ROUTE]);
            return;
        }
      });
  }

  // Modal Control Methods
  toggleMaximizeModal(): void {
    this.isModalMaximized = !this.isModalMaximized;
    if (this.isModalMaximized) {
      this.isModalMinimized = false;
    }
  }

  minimizeModal(): void {
    this.isModalMinimized = !this.isModalMinimized;
    if (this.isModalMinimized) {
      this.isModalMaximized = false;
    }
  }

  onModalMouseDown(event: MouseEvent): void {
    if (this.isModalMaximized) return;
    
    this.isModalResizing = true;
    this.modalStartX = event.clientX;
    this.modalStartY = event.clientY;
    
    const modalElement = event.target as HTMLElement;
    const modalContent = modalElement.closest('.modal-content') as HTMLElement;
    if (modalContent) {
      const rect = modalContent.getBoundingClientRect();
      this.modalStartWidth = rect.width;
      this.modalStartHeight = rect.height;
      
      // Store the resize direction based on the handle class
      const resizeDirection = this.getResizeDirection(modalElement);
      modalContent.setAttribute('data-resize-direction', resizeDirection);
    }
    
    document.addEventListener('mousemove', this.onModalMouseMove.bind(this));
    document.addEventListener('mouseup', this.onModalMouseUp.bind(this));
    
    event.preventDefault();
  }

  private getResizeDirection(element: HTMLElement): string {
    const classList = element.classList;
    if (classList.contains('resize-handle-se')) return 'se';
    if (classList.contains('resize-handle-sw')) return 'sw';
    if (classList.contains('resize-handle-ne')) return 'ne';
    if (classList.contains('resize-handle-nw')) return 'nw';
    if (classList.contains('resize-handle-s')) return 's';
    if (classList.contains('resize-handle-n')) return 'n';
    if (classList.contains('resize-handle-e')) return 'e';
    if (classList.contains('resize-handle-w')) return 'w';
    return 'se'; // default
  }

  onModalMouseMove(event: MouseEvent): void {
    if (!this.isModalResizing || this.isModalMaximized) return;
    
    const deltaX = event.clientX - this.modalStartX;
    const deltaY = event.clientY - this.modalStartY;
    
    const modalContent = document.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      const direction = modalContent.getAttribute('data-resize-direction') || 'se';
      let newWidth = this.modalStartWidth;
      let newHeight = this.modalStartHeight;
      
      // Handle horizontal resizing
      if (direction.includes('e')) {
        newWidth = Math.max(400, this.modalStartWidth + deltaX);
      } else if (direction.includes('w')) {
        newWidth = Math.max(400, this.modalStartWidth - deltaX);
      }
      
      // Handle vertical resizing
      if (direction.includes('s')) {
        newHeight = Math.max(300, this.modalStartHeight + deltaY);
      } else if (direction.includes('n')) {
        newHeight = Math.max(300, this.modalStartHeight - deltaY);
      }
      
      modalContent.style.width = `${newWidth}px`;
      modalContent.style.height = `${newHeight}px`;
      modalContent.style.maxWidth = 'none';
      modalContent.style.maxHeight = 'none';
    }
  }

  onModalMouseUp(): void {
    this.isModalResizing = false;
    document.removeEventListener('mousemove', this.onModalMouseMove.bind(this));
    document.removeEventListener('mouseup', this.onModalMouseUp.bind(this));
  }

  onModalKeyDown(event: KeyboardEvent): void {
    // ESC to close
    if (event.key === 'Escape') {
      this.closeProjectModal('ESC key pressed');
    }
    // F11 or F to toggle fullscreen
    else if (event.key === 'F11' || (event.key === 'f' || event.key === 'F')) {
      event.preventDefault();
      this.toggleMaximizeModal();
    }
  }

  resetModalSize(): void {
    const modalContent = document.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.style.width = '';
      modalContent.style.height = '';
      modalContent.style.maxWidth = '';
      modalContent.style.maxHeight = '';
    }
  }

  // Prevent modal from closing when clicking on interactive elements

  // ── Customer switcher ────────────────────────────────────────────────────


  showCustomerSwitcher(): boolean {
    return (this.isAdmin || this.isSuperAdmin) &&
      this.authService.availableCustomers.length > 1;
  }

  // Above this many companies, use a compact dropdown instead of a quick-pick list.
  get hasManyCustomers(): boolean {
    return this.authService.availableCustomers.length > 5;
  }

  get activeCustomerId(): string {
    return this.authService.getActiveCustomerId();
  }

  // Composite identity for a customer row: the same CustomerId can appear under
  // several tenants (see customer-selector.component.ts), so CustomerId alone is
  // ambiguous as a <select>/comparison key.
  keyFor(c: ActiveCustomer): string {
    return `${c.CustomerId}|${c.TenantId}`;
  }

  get activeCustomerKey(): string {
    const active = this.authService.getActiveCustomer();
    return active ? this.keyFor(active) : '';
  }

  // Names that occur under more than one tenant get a tenant discriminator in the UI.
  private get duplicatedCustomerNames(): Set<string> {
    const counts = new Map<string, number>();
    for (const c of this.authService.availableCustomers) {
      counts.set(c.CustomerName, (counts.get(c.CustomerName) ?? 0) + 1);
    }
    const dups = new Set<string>();
    for (const [name, n] of counts) {
      if (n > 1) dups.add(name);
    }
    return dups;
  }

  labelFor(c: ActiveCustomer): string {
    if (this.duplicatedCustomerNames.has(c.CustomerName)) {
      const discriminator = c.TenantName || `tenant ${c.TenantId.slice(0, 8)}`;
      return `${c.CustomerName} — ${discriminator}`;
    }
    return c.CustomerName;
  }

  onCompanySelect(key: string): void {
    const customer = this.authService.availableCustomers.find(
      (c) => this.keyFor(c) === key
    );
    if (customer) {
      this.switchCustomer(customer);
    }
  }

  switchCustomer(customer: ActiveCustomer): void {
    this.authService.setActiveCustomer(customer);
    this.userDropdownOpen = false;
    // Clear cached project data
    this.projectService.projectList = [];
    this.selectedProject = '';
    // CLEAR IT IN THE SERVICE TOO, or the tabs stay armed for a project that
    // belongs to the company we just left. projectSelected now asks the service,
    // so clearing only the local field would leave the strip enabled and pointing
    // at another company's cure — the exact cross-company leak the list and
    // localStorage clears above exist to prevent.
    this.projectService.setProject('');
    localStorage.removeItem('lastSelectedProject');
    // Reload project list for the newly selected customer
    const objectId = this.authService.getObjectId();
    const tenantId = this.authService.getCurrentCustomerTenantId();
    this.projectService.loadProjectList(objectId, tenantId);
    // Reload preferences so company name/defaults reflect the newly selected customer
    this.preferencesService.refresh();
    // LAND ON THE PROJECT LIST FOR THE NEW COMPANY. Nothing else.
    //
    // This used to be `navigate(['/']).then(() => this.openProjectModal())`, and
    // the comment beside it said it was opening "the picker". It is not, and has
    // not been since the picker became a route: `showProjectModal` is the UPLOAD
    // PROJECT modal — its header is literally MainLayoutUploadProject and the only
    // tab body left in it is `activeTab === 'upload'`. The project list is
    // ProjectSelectionComponent at `/projects`, which `/` already redirects to.
    //
    // So every company switch dropped an Upload Project dialog on top of the list
    // and the operator had to dismiss it to get anywhere — reported from the demo
    // bench as "switching companies takes you to the import project tab, which
    // blocks wherever you were before". The navigate was already correct; the
    // modal call was the whole defect.
    //
    // WHY THE LIST AND NOT THE PREVIOUS SCREEN: the switch deliberately clears
    // projectList and selectedProject just above, because the new company's
    // projects are a different set. Returning someone to /temperature with no
    // project selected would show an empty chart, which is worse than an honest
    // list. Someone on the project list gets the project list for the new
    // company, which is the intended behaviour.
    this._router.navigate(['/']);
  }

  // Restore the last selected project from localStorage after page refresh
  /** Feedback for the copy-link button; cleared on a timer. */
  shareLinkCopiedFor: string | null = null;

  /**
   * Open the project named in ?project=<name>.
   *
   * Mirrors restoreProjectOrShowPicker, with one deliberate difference: that one
   * waits a flat 1000 ms and gives up, which is a race it usually wins. A deep
   * link is someone else's URL opening on a cold load — often a fresh sign-in
   * first — so it POLLS for the list instead of assuming a fixed arrival time.
   * Losing that race would drop the user on an empty picker with no clue why.
   */
  private openProjectFromDeepLink(params: Record<string, unknown>): void {
    const requested = readProjectParam(params);
    if (!requested || this.deepLinkHandled === requested) {
      return;
    }
    // queryParams re-emits on navigation; only act once per name.
    this.deepLinkHandled = requested;

    const deadline = Date.now() + MainLayoutComponent.DEEP_LINK_TIMEOUT_MS;
    const attempt = () => {
      const outcome = resolveDeepLink(params, this.projectService.projectList);

      if (outcome.status === 'opened') {
        console.log('Main Layout > deep link opening project:', outcome.projectName);
        this.onProjectChange(outcome.projectName);
        return;
      }

      if (this.projectService.projectList.length === 0 && Date.now() < deadline) {
        setTimeout(attempt, MainLayoutComponent.DEEP_LINK_POLL_MS);
        return;
      }

      // The list arrived and the name is not in it. That is the company-scoping
      // check doing its job as much as it is a stale link, and the two are
      // indistinguishable from here — so say neither, and show the LIST.
      //
      // THE SAME MISTAKE f9ef06e FIXED AT THE COMPANY SWITCHER, in a second
      // place. This called openProjectModal() under a comment saying "show the
      // picker". It is not the picker: showProjectModal is the UPLOAD PROJECT
      // modal, and the picker has been a route (/projects) since long before
      // either line was written. So a deep link to a project this company cannot
      // see dropped an upload dialog over the app — which is exactly the symptom
      // reported for the switcher, reached by a different door.
      //
      // Reproduced on prod2 AFTER f9ef06e was deployed there: opening
      // /temperature?project=S3077 (the real name is S3077-2025-07-10_10-27-01)
      // covered the whole viewport with the Upload Project dialog.
      console.log(
        'Main Layout > deep-linked project not in this list:',
        outcome.status === 'not-found' ? outcome.projectName : requested
      );
      this._router.navigate([SELECTION_ROUTE]);
    };

    attempt();
  }

  /**
   * The deep-linked project, read from the BROWSER URL.
   *
   * Deliberately not the router snapshot: this is consulted from the MSAL
   * account callback, which can run before the router's initial navigation has
   * settled, and an empty snapshot there is what let persistence win.
   */
  /**
   * Any project link seen this session, from any source.
   *
   * A latch rather than a single read, because the three sources each miss a
   * case on their own: the module-scope capture only sees a HARD load, the
   * queryParams subscription only sees router navigations, and window.location
   * only reflects the present moment — which another navigation may already have
   * rewritten. Latching keeps the user's request alive across all three.
   */
  private deepLinkedProject: string | null = null;

  private deepLinkedProjectName(): string | null {
    try {
      return projectParamFromSearch(window.location?.search);
    } catch {
      return null;
    }
  }

  /** Guards against re-handling the same name when queryParams re-emits. */
  private deepLinkHandled: string | null = null;
  private static readonly DEEP_LINK_POLL_MS = 250;
  private static readonly DEEP_LINK_TIMEOUT_MS = 15000;


  // ── Project upload ────────────────────────────────────────────────────────

  /**
   * Upload state machine. Four states rather than a pair of booleans, because
   * "not uploading" and "failed" are different things to the person looking at
   * the screen and must not collapse into the same blank panel.
   */
  uploadPhase: 'idle' | 'working' | 'error' | 'done' = 'idle';
  uploadFile: File | null = null;
  uploadStatusText = '';
  uploadDetailText = '';
  uploadErrorMessage = '';
  uploadErrorDetail = '';
  uploadedProjectName = '';
  uploadedSummary = '';

  get uploadFileSize(): string {
    return this.uploadFile ? formatBytes(this.uploadFile.size) : '';
  }

  /** Validate at PICK time so a wrong file is caught before any bytes move. */
  onUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] ?? null;

    const invalid = validateFile(file);
    if (invalid) {
      this.uploadFile = null;
      this.failUpload(invalid);
      return;
    }
    this.uploadFile = file;
    this.uploadErrorMessage = '';
    this.uploadErrorDetail = '';
  }

  /**
   * Take a file chosen elsewhere and run it through the existing upload.
   *
   * Validates FIRST, exactly as the in-modal input does: the browser's accept
   * filter is a convenience, not a guarantee — a user can defeat it by typing a
   * name — so the same check runs regardless of which door the file came in
   * through. A rejected file still opens the modal, because the error has to be
   * somewhere the user can read it.
   */
  beginUploadWithFile(file: File): void {
    this.activeTab = 'upload';
    this.openProjectModal();

    const invalid = validateFile(file);
    if (invalid) {
      this.uploadFile = null;
      this.failUpload(invalid);
      return;
    }
    this.uploadFile = file;
    this.uploadErrorMessage = '';
    this.uploadErrorDetail = '';
    void this.startUpload();
  }

  async startUpload(): Promise<void> {
    const file = this.uploadFile;
    if (!file) {
      return;
    }

    this.uploadPhase = 'working';
    this.uploadStatusText = 'Preparing upload…';
    this.uploadDetailText = '';
    this.projUploading = true;
    this.projectLoadPerc = 0;

    try {
      const result = await this.uploadService.upload(file, (p: UploadProgress) =>
        this.onUploadProgress(p)
      );
      // Prefer the name the SERVER parsed out of the project over the filename —
      // they can differ, and the Existing list will show the server's.
      this.uploadedProjectName = result.import?.projectName || result.projectName;
      this.uploadedSummary = result.import
        ? `${result.import.ticksImported} ticks from device ${result.import.serial}`
        : '';
      this.uploadPhase = 'done';
      this.projUploading = false;
      this.projectLoadPerc = 0;

      // Refresh so the new project appears in Existing without a manual reload.
      this.initializeProjects();
    } catch (err) {
      this.failUpload(err);
    }
  }

  cancelUpload(): void {
    this.uploadService.cancel();
    this.uploadStatusText = 'Cancelling…';
  }

  resetUpload(): void {
    this.uploadPhase = 'idle';
    this.uploadFile = null;
    this.uploadErrorMessage = '';
    this.uploadErrorDetail = '';
    this.projUploading = false;
    this.projectLoadPerc = 0;
  }

  /** Open the project that was just uploaded. */
  goToUploadedProject(): void {
    if (this.uploadedProjectName) {
      this.onProjectChange(this.uploadedProjectName, true);
      this.closeProjectModal();
    }
  }

  private onUploadProgress(p: UploadProgress): void {
    this.projectLoadPerc = p.percent;
    this.uploadDetailText = `part ${p.blocksDone} of ${p.blocksTotal}`;
    switch (p.phase) {
      case 'preparing':
        this.uploadStatusText = 'Preparing upload…';
        break;
      case 'uploading':
        this.uploadStatusText = `Uploading ${this.uploadFile?.name ?? 'project'}…`;
        break;
      case 'committing':
        this.uploadStatusText = 'Finalising upload…';
        break;
      case 'importing':
        this.uploadStatusText = 'Importing project…';
        break;
      case 'done':
        this.uploadStatusText = 'Done';
        break;
    }
  }

  /**
   * Surface a failure. NO SILENT FAIL: every path into this leaves a message on
   * screen, and an unrecognised error still produces something a person can read
   * rather than an empty panel.
   */
  private failUpload(err: unknown): void {
    this.projUploading = false;
    this.projectLoadPerc = 0;
    this.uploadPhase = 'error';

    if (err instanceof UploadError) {
      this.uploadErrorMessage = err.message;
      this.uploadErrorDetail = err.detail ?? '';
      console.error('Upload failed:', err.kind, err.message, err.detail ?? '');
      return;
    }
    this.uploadErrorMessage =
      'The upload failed for an unexpected reason. Please try again, and tell the Vericure team if it keeps happening.';
    this.uploadErrorDetail = err instanceof Error ? err.message : String(err ?? '');
    console.error('Upload failed (unrecognised):', err);
  }

  /**
   * Manual sign-in, for when automatic recovery is spent.
   *
   * The automatic path is the interceptor's: a failed acquisition redirects
   * immediately, carrying the current URL so the user returns here. This button
   * exists for the terminal case — the budget is exhausted and bouncing again
   * would be a loop — so it goes through the same login the app uses everywhere
   * rather than a second, subtly different flow.
   */
  signInAgain(): void {
    this.authService.login();
  }



}
